import { LocalDate, ZonedDateTime } from '@js-joda/core';
import * as dayjs from 'dayjs';
import { JDateUtils } from './jdate-utils';
import { FieldConfig, FieldDataTypes } from '@hidat/huijs-common';

/**
 * Field Serializer
 * A greatly simplified serialization class, based on the typing and property naming contained in a 'FieldConfig'.
 * The first order of business for this class is to help pull data in and out of forms, but it can also be used for JSON.
 * DOES NOT handle arrays or embedded objects directly (although they could be handled by custom serializers.
 */
export class FieldConverter {
  /**
   * To Integer
   * Converts src to an integer number
   * Only supports Number and string sources.
   * Has same results as parseInt.
   * @param src
   */
  static toInt(src: unknown): number | undefined {
    let res: number | undefined;
    if (src !== undefined) {
      // First check if we already have the type we want, and don't have to do anything
      if (typeof src === 'number') {
        res = Math.trunc(src);
      } else if (typeof src === 'string') {
        res = parseInt(src);
      }
    }
    return res;
  }

  /**
   * To Numeric
   * Converts src to a number.
   * Only supports Number and string sources.
   * Has same results as parseFloat.
   * @param src
   */
  static toNumeric(src: unknown): number | undefined {
    let res: number | undefined;

    if (src !== undefined) {
      // First check if we already have the type we want, and don't have to do anything
      if (typeof src === 'number') {
        res = src;
      } else if (typeof src === 'string') {
        res = parseFloat(src);
      }
    }
    return res;
  }

  /**
   * To String
   * If src is a date, converts to ISO date, else simply calls toString on src.
   * @param src
   */
  static toStr(src: unknown): string | undefined {
    let res: string | undefined;

    if (src !== undefined) {
      // First check if we already have the type we want, and don't have to do anything
      if (typeof src === 'string') {
        res = src;
      } else if (src instanceof LocalDate) {
        res = JDateUtils.toLocalDateString(src);
      } else if (
        src instanceof Date ||
        src instanceof ZonedDateTime ||
        dayjs.isDayjs(src)
      ) {
        res = JDateUtils.toIsoDateStr(src);
      } else {
        const fb = src as any;
        if (fb['toString']) {
          res = fb.toString();
        }
      }
      // Always trim the end, maybe should be some type of a transformer upstream, but for now....
      if (res) {
        res = res.trimEnd();
      }
    }
    return res;
  }

  static toBoolean(src: unknown): boolean | undefined {
    let res: boolean | undefined;
    if (src !== undefined && src !== null) {
      // First check if we already have the type we want, and don't have to do anything
      if (typeof src === 'boolean') {
        res = src;
      } else if (typeof src === 'string') {
        const falsy = ['0', 'false', 'no'];
        res = !falsy.includes(src.toLowerCase());
      } else if (typeof src === 'number') {
        res = src !== 0;
      }
    }
    return res;
  }

  /**
   * Attempts to convert the 'src' into the given FieldDataType
   * @param src
   * @param type
   */
  static convertField(src: unknown, type: FieldDataTypes): any {
    let res: any;

    switch (type) {
      case FieldDataTypes.DATE:
        res = JDateUtils.toLocalDate(src);
        break;
      case FieldDataTypes.DATETIME:
        res = JDateUtils.toDateTime(src);
        break;
      case FieldDataTypes.JSDATE:
        res = JDateUtils.toJSDate(src);
        break;
      case FieldDataTypes.DAYJS:
        res = JDateUtils.toDayjs(src);
        break;
      case FieldDataTypes.INTEGER:
        res = this.toInt(src);
        break;
      case FieldDataTypes.NUMBER:
        res = this.toNumeric(src);
        break;
      case FieldDataTypes.STRING:
      case FieldDataTypes.UID:
        res = this.toStr(src);
        break;
      case FieldDataTypes.BOOLEAN:
        res = this.toBoolean(src);
        break;
      default:
        res = src;
        break;
    }

    return res;
  }

  /**
   * Converts the appropriate string, which will be either a Data only, or an ISO date
   * @constructor
   * @param val
   * @param dataType
   */
  static dateToString(
    val: unknown,
    dataType: FieldDataTypes,
  ): string | undefined {
    let res: string | undefined;
    if (val) {
      if (dataType === FieldDataTypes.DATE) {
        // Want to pass MMMM-DD-YY
        res = JDateUtils.toLocalDateString(val);
      } else if (dataType === FieldDataTypes.DATEZONED) {
        // Strip any time, we are just interested in the date and the timezone
        res = JDateUtils.toDateWithZoneStr(val);
      } else if (dataType === FieldDataTypes.DATETIME) {
        res = JDateUtils.toIsoDateStr(val);
      }
    }
    return res;
  }

  /**
   * To Hash
   * Converts the given item into a hash.
   * Checks each field, and if it is included in fieldConfigs, will serialize the field based on the FieldType.
   * @param fieldConfigs
   * @param item
   */
  /*
  static toHash(item: any, fieldConfigs: FieldConfig [] | Map<string, FieldConfig>) {
    let fcMap: Map<string, FieldConfig>;
    if (fieldConfigs instanceof Map) {
      fcMap = fieldConfigs;
    } else {
      fcMap = new Map<string, FieldConfig>()
    }
  }*/

  /**
   *
   * @param fieldConfigs
   * @param item
   */
  /*
  static fromHash(fieldConfigs: FieldConfig [] | Map<string, FieldConfig>, item?: any): any {
    item ??= {};

    return item;
  }*/

  fieldConfigs: Map<string, FieldConfig> = new Map();
}
